"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  ArrowRight,
  Bot,
  CircleHelp,
  Code2,
  Lock,
  ShieldCheck,
  Snowflake,
  Timer,
  Trophy,
  User,
  Users,
  Check,
  Link2,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format-datetime";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { SITE_URL } from "@/lib/site-url";
import { RichContent } from "@/components/rich-content";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  contestHostLine,
  contestZoneLabel,
  formatClockTime,
  formatDateTimeWithoutZone,
  formatDuration,
  sameDayInZone,
} from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import type { ContestCredit, PublicContestDetail } from "../types";
import { AuthorHandle } from "./AuthorHandle";
import { DivisionLine } from "./DivisionTag";

/** The freeze's length in minutes, or `null` without one. */
function freezeMinutesOf(contest: PublicContestDetail): number | null {
  if (!contest.freezeAt || !contest.endAt) return null;
  const minutes = Math.round((new Date(contest.endAt).getTime() - new Date(contest.freezeAt).getTime()) / 60_000);
  return minutes > 0 ? minutes : null;
}

function hasEnded(contest: PublicContestDetail): boolean {
  return contest.phase === "ended" || contest.phase === "unfrozen";
}

/** A section of the Overview: a white card with the small caps heading the canvas draws. */
function Section({
  title,
  aside,
  id,
  children,
  testId,
}: {
  title: string;
  aside?: ReactNode;
  id?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <Card id={id} data-testid={testId} className="scroll-mt-[140px] p-6 sm:p-7">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-[12.5px] font-bold uppercase tracking-[0.12em] text-neutral-500">{title}</h2>
        {aside}
      </div>
      <div className="mt-4">{children}</div>
    </Card>
  );
}

/* ── The timeline ─────────────────────────────────────────────────────────── */

type TimelineRow = {
  key: string;
  when: string;
  title: string;
  body: string | null;
  mark: "brand" | "plain" | "freeze" | "result";
  now: boolean;
};

function OverviewTimeline({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const zone = useContestDisplayZone();
  const freezeMinutes = freezeMinutesOf(contest);
  const ended = hasEnded(contest);
  const before = contest.phase === "draft" || contest.phase === "scheduled";

  // A later row on the start's own day prints its time; on another day, the date too.
  const whenOf = (iso: string) =>
    contest.startAt && sameDayInZone(iso, contest.startAt, zone)
      ? formatClockTime(iso, locale, zone)
      : formatDateTimeWithoutZone(iso, locale, zone);

  const rows: TimelineRow[] = [];
  if (!ended) {
    rows.push({
      key: "entry",
      when: before ? t("tlNow") : "",
      title: t(`tlEntry_${contest.registration}`),
      body: contest.registration === "open" ? t("tlEntryBody_open") : null,
      mark: "brand",
      now: before,
    });
  }
  if (contest.startAt) {
    rows.push({
      key: "start",
      when: formatDateTimeWithoutZone(contest.startAt, locale, zone),
      title: t("tlStart"),
      body: t("tlStartBody"),
      mark: "plain",
      now: contest.phase === "running",
    });
  }
  if (contest.freezeAt && freezeMinutes !== null) {
    rows.push({
      key: "freeze",
      when: whenOf(contest.freezeAt),
      title: t("tlFreeze"),
      body: t("tlFreezeBody"),
      mark: "freeze",
      now: contest.phase === "frozen",
    });
  }
  if (contest.endAt) {
    rows.push({
      key: "end",
      when: whenOf(contest.endAt),
      title: t("tlEnd"),
      body: t("tlEndBody", { time: formatClockTime(contest.endAt, locale, zone) }),
      mark: "plain",
      now: false,
    });
  }
  const resultsKey =
    freezeMinutes !== null
      ? contest.rated
        ? "tlResultsBody_frozenRated"
        : "tlResultsBody_frozen"
      : contest.rated
        ? "tlResultsBody_rated"
        : "tlResultsBody_plain";
  rows.push({
    key: "results",
    when: t("tlResultsWhen"),
    title: t("tlResults"),
    body: t(resultsKey),
    mark: "result",
    now: ended,
  });

  const DOT: Record<TimelineRow["mark"], string> = {
    brand: "border-primary",
    plain: "border-neutral-900",
    freeze: "border-sky-600",
    result: "border-emerald-700",
  };

  return (
    <Section title={t("overviewTimeline")} aside={<span className="text-[13px] text-neutral-500">{contestZoneLabel(locale, zone)}</span>}>
      <ol data-testid="contest-timeline" className="flex flex-col">
        {rows.map((row, index) => (
          <li key={row.key} data-now={row.now ? "true" : undefined} className="flex gap-4 sm:gap-[18px]">
            {/* From `sm` the time is a column of its own; on a phone it sits over the title (the canvas's phone board). */}
            <div
              className={cn(
                "hidden w-[104px] shrink-0 pt-0.5 text-[13px] leading-snug sm:block",
                row.now ? "font-bold uppercase tracking-wide text-primary-ink" : "font-medium text-neutral-600",
              )}
            >
              {row.now ? t("tlNow") : row.when}
            </div>
            <div className="flex shrink-0 flex-col items-center">
              <span
                aria-hidden
                className={cn(
                  "mt-[3px] h-3.5 w-3.5 rounded-full border-2",
                  row.now ? "border-primary bg-primary ring-4 ring-primary/20" : cn("bg-white", DOT[row.mark]),
                )}
              />
              {index < rows.length - 1 ? <span aria-hidden className="min-h-9 w-0.5 flex-1 bg-neutral-200" /> : null}
            </div>
            <div className={cn("flex flex-col gap-0.5", index < rows.length - 1 ? "pb-5" : null)}>
              <span
                className={cn(
                  "text-xs sm:hidden",
                  row.now ? "font-bold uppercase tracking-wide text-primary-ink" : "font-medium text-neutral-600",
                )}
              >
                {row.now ? t("tlNow") : row.when}
              </span>
              <b className="text-[15.5px] font-bold text-neutral-900">{row.title}</b>
              {row.body ? <span className="text-sm leading-relaxed text-neutral-600">{row.body}</span> : null}
              {row.now && row.key !== "entry" && row.when ? (
                <span className="text-[13px] text-neutral-500">{row.when}</span>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}

/* ── Certificates (Wave 30 decision 2) ────────────────────────────────────── */

function OverviewCertificates({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const certificates = contest.certificates;
  if (!certificates || (certificates.rankTopN === null && !certificates.participation)) return null;
  const top = certificates.rankTopN;

  const tiles: Array<{ key: string; rank: string; title: string; tone: string }> = [];
  if (top !== null && top >= 1) tiles.push({ key: "1", rank: t("certRank1"), title: t("certChampion"), tone: "border-amber-200 bg-amber-50" });
  if (top !== null && top >= 2) tiles.push({ key: "2", rank: t("certRank2"), title: t("certRunnerUp1"), tone: "border-neutral-200 bg-neutral-50" });
  if (top !== null && top >= 3) tiles.push({ key: "3", rank: t("certRank3"), title: t("certRunnerUp2"), tone: "border-orange-200 bg-orange-50" });
  if (top !== null && top >= 4) {
    tiles.push({
      key: "rest",
      rank: t("certRankRest", { from: formatNumber(4, locale), to: formatNumber(top, locale) }),
      title: t("certPlace"),
      tone: "border-neutral-200 bg-white",
    });
  }

  return (
    <Section title={t("overviewCertificates")} testId="contest-certificates">
      <div className="flex flex-col gap-1.5 text-[15.5px] leading-relaxed text-neutral-800">
        {top !== null ? <p>{t("certTop", { count: formatNumber(top, locale) })}</p> : null}
        {certificates.participation ? <p>{t("certEveryone")}</p> : null}
      </div>
      {tiles.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {tiles.map((tile) => (
            <div key={tile.key} className={cn("flex flex-col gap-1 rounded-xl border p-3.5", tile.tone)}>
              <span className="text-xs font-bold text-neutral-600">{tile.rank}</span>
              <b className="text-[15px] text-neutral-900">{tile.title}</b>
            </div>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

/* ── Rules at a glance ────────────────────────────────────────────────────── */

function OverviewRules({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const freezeMinutes = freezeMinutesOf(contest);
  const profile = contest.integrity?.profile ?? "honour";

  const items: Array<{ key: string; icon: ReactNode; lead: string; rest: string }> = [];
  const icon = (node: ReactNode) => <span className="text-primary">{node}</span>;
  if (contest.scoringFormat === "icpc") {
    items.push({ key: "format", icon: icon(<Trophy className="h-[18px] w-[18px]" />), lead: t("ruleIcpcLead"), rest: t("ruleIcpc") });
    items.push({ key: "penalty", icon: icon(<Timer className="h-[18px] w-[18px]" />), lead: t("rulePenaltyLead"), rest: t("rulePenalty") });
  } else if (contest.scoringFormat === "codeforces") {
    items.push({
      key: "format",
      icon: icon(<Trophy className="h-[18px] w-[18px]" />),
      lead: t("ruleCodeforcesLead"),
      rest: t("ruleCodeforces"),
    });
  } else {
    items.push({ key: "format", icon: icon(<Trophy className="h-[18px] w-[18px]" />), lead: t("rulePointsLead"), rest: t("rulePoints") });
  }
  items.push({ key: "compile", icon: icon(<Check className="h-[18px] w-[18px]" />), lead: t("ruleCompileLead"), rest: t("ruleCompile") });
  if (freezeMinutes !== null) {
    items.push({
      key: "freeze",
      icon: icon(<Snowflake className="h-[18px] w-[18px]" />),
      lead: t("ruleFreezeLead"),
      rest: t("ruleFreeze", { minutes: formatNumber(freezeMinutes, locale) }),
    });
  }
  items.push(
    contest.participation === "team"
      ? { key: "who", icon: icon(<Users className="h-[18px] w-[18px]" />), lead: t("ruleTeamLead"), rest: t("ruleTeam") }
      : { key: "who", icon: icon(<User className="h-[18px] w-[18px]" />), lead: t("ruleIndividualLead"), rest: t("ruleIndividual") },
  );
  items.push({
    key: "ai",
    icon: icon(<Bot className="h-[18px] w-[18px]" />),
    lead: t(`ruleAiLead_${contest.aiPolicy}`),
    rest: t(`ruleAi_${contest.aiPolicy}`),
  });
  items.push({
    key: "integrity",
    icon: icon(<ShieldCheck className="h-[18px] w-[18px]" />),
    lead: t("ruleIntegrityLead"),
    rest: t(`ruleIntegrity_${profile}`),
  });
  // `EX-SRC02`, ADR-0134: told before anybody submits, never only after the end.
  if (contest.submissionsPublished === true) {
    items.push({ key: "code", icon: icon(<Code2 className="h-[18px] w-[18px]" />), lead: t("ruleCodeLead"), rest: t("ruleCode") });
  }

  return (
    <Section title={t("overviewRules")} testId="contest-rules-glance">
      <ul className="grid gap-x-7 gap-y-3.5 text-[15px] leading-relaxed text-neutral-800 md:grid-cols-2">
        {items.map((item) => (
          <li key={item.key} className="flex gap-3">
            <span aria-hidden className="shrink-0 pt-0.5">
              {item.icon}
            </span>
            <span>
              <b className="font-bold text-neutral-900">{item.lead}</b> {item.rest}
            </span>
          </li>
        ))}
      </ul>
      <Link
        href={`/${locale}/contests/${contest.slug}/rules`}
        className="mt-5 inline-flex min-h-11 items-center gap-1.5 text-[14.5px] font-bold text-primary-ink transition-colors duration-200 hover:text-primary"
      >
        {t("ruleRulesLink")}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </Section>
  );
}

/* ── Authors and testers ──────────────────────────────────────────────────── */

function PersonAvatar({ person, size }: { person: ContestCredit; size: "md" | "sm" }) {
  const box = size === "md" ? "h-12 w-12 text-lg" : "h-10 w-10 text-base";
  return person.avatarImageId ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={cfImageUrl(person.avatarImageId, "avatar")} alt="" className={cn("shrink-0 rounded-full object-cover", box)} />
  ) : (
    <span aria-hidden className={cn("flex shrink-0 items-center justify-center rounded-full bg-primary font-bold uppercase text-white", box)}>
      {(person.name || person.username || "?").charAt(0)}
    </span>
  );
}

function CreditCard({ person, locale, tag }: { person: ContestCredit; locale: string; tag: string | null }) {
  return (
    <div className="flex w-full min-w-0 items-center gap-3.5 rounded-xl border border-neutral-200 bg-white py-3.5 pl-3.5 pr-4 sm:w-auto sm:min-w-[300px]">
      <PersonAvatar person={person} size="md" />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <b className="text-[15.5px] text-neutral-900">{person.name || person.username}</b>
          {tag ? (
            <span className="rounded-full border border-neutral-200 px-2.5 py-0.5 text-xs font-bold text-neutral-600">{tag}</span>
          ) : null}
        </span>
        {person.username || person.institution ? (
          <span className="break-words text-sm text-neutral-600">
            {person.username ? <AuthorHandle person={person} locale={locale} tone="light" /> : null}
            {person.username && person.institution ? " · " : null}
            {person.institution}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function OverviewAuthors({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  if (contest.authors.length === 0 && contest.testers.length === 0) return null;
  return (
    <Section title={t("overviewAuthors")} id="authors" testId="contest-authors">
      {contest.authors.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {contest.authors.map((author) => (
            <CreditCard
              key={author.username ?? author.name}
              person={author}
              locale={locale}
              tag={author.lead ? t("leadTag") : t("authorTag")}
            />
          ))}
        </div>
      ) : null}
      {contest.testers.length > 0 ? (
        <>
          <h3 className={cn("text-sm font-bold text-neutral-900", contest.authors.length > 0 ? "mt-5" : null)}>
            {t("overviewTesters")}
          </h3>
          <div className="mt-3 flex flex-wrap gap-3">
            {contest.testers.map((tester) => (
              <CreditCard key={tester.username ?? tester.name} person={tester} locale={locale} tag={null} />
            ))}
          </div>
        </>
      ) : null}
    </Section>
  );
}

/* ── The sidebar ──────────────────────────────────────────────────────────── */

function SideCard({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <Card data-testid={testId} className="overflow-hidden">
      <h2 className="border-b border-neutral-200 px-5 py-4 text-[12.5px] font-bold uppercase tracking-[0.12em] text-neutral-500">
        {title}
      </h2>
      <div className="px-5 py-4">{children}</div>
    </Card>
  );
}

function ContestDetails({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const tCommon = useTranslations("contest");
  const freezeMinutes = freezeMinutesOf(contest);
  const duration = formatDuration(contest.startAt, contest.endAt, locale);

  const rows: Array<{ key: string; label: string; value: ReactNode }> = [
    { key: "format", label: t("detailFormat"), value: tCommon(`scoring.${contest.scoringFormat}`) },
    ...(duration ? [{ key: "length", label: t("detailLength"), value: duration }] : []),
    { key: "type", label: t("detailType"), value: tCommon(`participation.${contest.participation}`) },
    {
      key: "rated",
      label: t("detailRated"),
      value: contest.rated ? (
        contest.division ? (
          <DivisionLine division={contest.division} locale={locale} className="text-right" />
        ) : (
          t("detailRatedYes")
        )
      ) : (
        t("detailRatedNo")
      ),
    },
    {
      key: "freeze",
      label: t("detailFreeze"),
      value: freezeMinutes !== null ? t("detailFreezeValue", { minutes: formatNumber(freezeMinutes, locale) }) : t("detailFreezeNone"),
    },
    { key: "ai", label: t("detailAi"), value: t(`detailAiValue_${contest.aiPolicy}`) },
    {
      key: "entry",
      label: t("detailEntry"),
      value: hasEnded(contest)
        ? t("detailEntryValue_closed")
        : contest.registration === "open"
          ? t("detailEntryValue_open")
          : tCommon(`registration.${contest.registration}`),
    },
    ...(typeof contest.seatLimit === "number"
      ? [
          {
            key: "seats",
            label: t("detailSeats"),
            value: t("detailSeatsValue", {
              taken: formatNumber(contest.participantCount, locale),
              limit: formatNumber(contest.seatLimit, locale),
            }),
          },
        ]
      : []),
  ];

  return (
    <SideCard title={t("detailsTitle")} testId="contest-details">
      <dl className="-my-1.5 grid grid-cols-[auto_minmax(0,1fr)] text-[14.5px]">
        {rows.map((row, index) => (
          <div key={row.key} className={cn("contents", index < rows.length - 1 ? "[&>*]:border-b [&>*]:border-neutral-100" : null)}>
            <dt className="py-2.5 pr-4 text-neutral-600">{row.label}</dt>
            <dd className="py-2.5 text-right font-medium text-neutral-900">{row.value}</dd>
          </div>
        ))}
      </dl>
    </SideCard>
  );
}

function ContestOrganizer({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const hostLine = contestHostLine(contest, locale);
  const coHost =
    contest.official && contest.hostOrganization
      ? (locale === "bn" ? contest.hostOrganization.name_bn : null) ?? contest.hostOrganization.name_en
      : null;

  let body: ReactNode = null;
  if (contest.official) {
    body = (
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary text-white">
          <Trophy className="h-5 w-5" aria-hidden />
        </span>
        <span className="flex flex-col gap-0.5">
          <b className="text-[15px] text-neutral-900">Progsity</b>
          <span className="text-[13.5px] text-neutral-600">
            {coHost ? t("organizerWith", { name: coHost }) : t("organizerProgsity")}
          </span>
        </span>
      </div>
    );
  } else if (hostLine?.kind === "organization") {
    body = (
      <div className="flex items-center gap-3">
        {hostLine.logoImageId ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={cfImageUrl(hostLine.logoImageId, "avatar")} alt="" className="h-10 w-10 shrink-0 rounded-[10px] border border-neutral-200 bg-white object-contain p-1" />
        ) : null}
        <b className="text-[15px] text-neutral-900">{hostLine.name}</b>
      </div>
    );
  } else if (hostLine) {
    const person: ContestCredit = {
      name: hostLine.name,
      username: hostLine.person.username,
      avatarImageId: hostLine.person.avatarImageId,
    };
    body = (
      <div className="flex items-center gap-3">
        <PersonAvatar person={person} size="sm" />
        <span className="flex min-w-0 flex-col gap-0.5">
          <b className="truncate text-[15px] text-neutral-900">{hostLine.name}</b>
          {person.username ? (
            <span className="text-[13.5px]">
              <AuthorHandle person={person} locale={locale} tone="light" />
            </span>
          ) : null}
        </span>
      </div>
    );
  }
  if (!body) return null;
  return (
    <SideCard title={t("organizerTitle")} testId="contest-organizer">
      {body}
    </SideCard>
  );
}

function ContestProblemsCard({
  contest,
  locale,
  onShowProblems,
  onOpenGuide,
}: {
  contest: PublicContestDetail;
  locale: string;
  onShowProblems: () => void;
  onOpenGuide: () => void;
}) {
  const t = useTranslations("contest.page");
  const zone = useContestDisplayZone();
  const before = contest.phase === "draft" || contest.phase === "scheduled";

  return (
    <SideCard title={t("problemsTitle")} testId="contest-problems-card">
      {contest.problems === null ? (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Lock className="h-4 w-4" aria-hidden />
            </span>
            <b className="text-[14.5px] text-neutral-900">
              {contest.startAt
                ? t("problemsOpenWhen", { when: formatDateTimeWithoutZone(contest.startAt, locale, zone) })
                : t("problemsLockedTitle")}
            </b>
          </div>
          <p className="text-[13.5px] leading-relaxed text-neutral-600">{t("problemsLockedShort")}</p>
          {before ? (
            <Button type="button" variant="outline" size="sm" className="self-start" onClick={onOpenGuide}>
              <CircleHelp className="h-4 w-4 text-primary" aria-hidden />
              {t("howSubmitting")}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-[14.5px] text-neutral-800">
            {t("problemsCount", { n: contest.problems.length, count: formatNumber(contest.problems.length, locale) })}
          </p>
          <Button type="button" variant="outline" size="sm" className="self-start" onClick={onShowProblems}>
            {t("problemsGoTo")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      )}
    </SideCard>
  );
}

/** Plain share links: no third-party script, only the page's own address (`AGENTS.md` §10). */
function ContestShare({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page");
  const [copied, setCopied] = useState(false);
  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;
  const url = `${SITE_URL}/${locale}/contests/${contest.slug}`;
  const text = t("shareText", { title });
  const encoded = encodeURIComponent(url);
  const links = [
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}` },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}` },
    { key: "x", label: "X", href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodeURIComponent(text)}` },
  ];

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <SideCard title={t("shareTitle")} testId="contest-share">
      <div className="flex flex-col gap-3">
        <Button type="button" variant="outline" className="w-full" onClick={() => void copy()}>
          {copied ? <Check className="h-4 w-4 text-primary" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
          <span aria-live="polite">{copied ? t("shareCopied") : t("shareCopy")}</span>
        </Button>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
          {links.map((link) => (
            <Button key={link.key} asChild variant="secondary" size="sm" className="w-full">
              <a href={link.href} target="_blank" rel="noopener noreferrer">
                {link.label}
              </a>
            </Button>
          ))}
        </div>
      </div>
    </SideCard>
  );
}

/**
 * The contest page's Overview (`EX-CPG02`, Wave 30 decision 3, `docs/29` §5):
 * what a reader who opens a contest wants to know before it starts, Toph's
 * contest page and the canvas's Detail board. About (the description, editor
 * HTML), the timeline, the certificates, the rules at a glance and the
 * authors; beside them the details, who runs it, the problems' lock and the
 * share links. Every fact is the page payload's own; nothing here decides who
 * may do what.
 */
export function ContestOverview({
  contest,
  locale,
  onShowProblems,
  onOpenGuide,
}: {
  contest: PublicContestDetail;
  locale: string;
  onShowProblems: () => void;
  onOpenGuide: () => void;
}) {
  const t = useTranslations("contest.page");
  const description = (locale === "bn" ? contest.description_bn : null) ?? contest.description_en;

  return (
    <div data-testid="contest-overview" className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="flex min-w-0 flex-col gap-5">
        {/*
          `ProgsityEditor` HTML since 2026-09-18 (`docs/21` §12), sanitised on
          write and again here by `RichContent`, which is defence in depth and
          not redundancy.
        */}
        {description ? (
          <Section title={t("overviewAbout")} testId="contest-about">
            <RichContent html={description} className="max-w-[760px]" />
          </Section>
        ) : null}
        <OverviewTimeline contest={contest} locale={locale} />
        <OverviewCertificates contest={contest} locale={locale} />
        <OverviewRules contest={contest} locale={locale} />
        <OverviewAuthors contest={contest} locale={locale} />
      </div>
      <aside className="flex min-w-0 flex-col gap-5">
        <ContestDetails contest={contest} locale={locale} />
        <ContestOrganizer contest={contest} locale={locale} />
        <ContestProblemsCard contest={contest} locale={locale} onShowProblems={onShowProblems} onOpenGuide={onOpenGuide} />
        <ContestShare contest={contest} locale={locale} />
      </aside>
    </div>
  );
}
